import { TrendingUp, TrendingDown, DollarSign, Package, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

export default function ScenarioComparison({ scenarios, onClose }) {
  const formatCurrency = (cents) => {
    if (!cents) return '$0';
    const value = Math.abs(cents) / 100;
    const formatted = value >= 1000000 
      ? `$${(value / 1000000).toFixed(1)}M`
      : value >= 1000
      ? `$${(value / 1000).toFixed(0)}K`
      : `$${value.toFixed(0)}`;
    return cents < 0 ? `-${formatted}` : formatted;
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: <Badge className="bg-gray-500">Draft</Badge>,
      active: <Badge className="bg-green-600">Active</Badge>,
      partially_active: <Badge className="bg-amber-600">Partially Active</Badge>,
      not_active: <Badge className="bg-red-500">Inactive</Badge>,
      archived: <Badge className="bg-gray-400">Archived</Badge>
    };
    return badges[status] || <Badge>{status}</Badge>;
  };

  return (
    <div className="h-full overflow-auto p-6 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Compare Scenarios</h2>
            <p className="text-sm text-gray-600 mt-1">
              Comparing {scenarios.length} scenario{scenarios.length > 1 ? 's' : ''}
            </p>
          </div>
          <Button
            onClick={onClose}
            variant="outline"
            size="sm"
          >
            <X className="h-4 w-4 mr-2" />
            Close Comparison
          </Button>
        </div>

        {/* Comparison Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 w-48">
                  Metric
                </th>
                {scenarios.map((scenario, idx) => (
                  <th key={scenario.id} className="px-4 py-3 text-left text-sm font-semibold text-gray-900">
                    <div className="flex flex-col">
                      <span className="font-bold text-base">{scenario.name}</span>
                      <span className="text-xs font-normal text-gray-600 mt-1">
                        {scenario.opportunity_name}
                      </span>
                      <div className="mt-1">{getStatusBadge(scenario.status)}</div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {/* Revenue Metrics */}
              <tr className="bg-blue-50">
                <td colSpan={scenarios.length + 1} className="px-4 py-2 text-sm font-semibold text-blue-900">
                  <DollarSign className="inline h-4 w-4 mr-1" />
                  Revenue Metrics
                </td>
              </tr>
              
              <tr>
                <td className="px-4 py-3 text-sm text-gray-700 font-medium">Baseline Revenue</td>
                {scenarios.map(scenario => (
                  <td key={scenario.id} className="px-4 py-3 text-sm text-gray-900">
                    {formatCurrency(scenario.revenue_baseline_cents)}
                  </td>
                ))}
              </tr>
              
              <tr className="bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-700 font-medium">Scenario Revenue</td>
                {scenarios.map(scenario => (
                  <td key={scenario.id} className="px-4 py-3 text-sm text-gray-900 font-semibold">
                    {formatCurrency(scenario.revenue_scenario_cents)}
                  </td>
                ))}
              </tr>
              
              <tr>
                <td className="px-4 py-3 text-sm text-gray-700 font-medium">Revenue Impact</td>
                {scenarios.map(scenario => (
                  <td key={scenario.id} className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-bold ${
                        scenario.revenue_delta_cents > 0 ? 'text-green-600' : 
                        scenario.revenue_delta_cents < 0 ? 'text-red-600' : 
                        'text-gray-600'
                      }`}>
                        {formatCurrency(scenario.revenue_delta_cents)}
                      </span>
                      {scenario.revenue_delta_cents > 0 && (
                        <TrendingUp className="h-4 w-4 text-green-600" />
                      )}
                      {scenario.revenue_delta_cents < 0 && (
                        <TrendingDown className="h-4 w-4 text-red-600" />
                      )}
                    </div>
                  </td>
                ))}
              </tr>
              
              <tr className="bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-700 font-medium">Impact %</td>
                {scenarios.map(scenario => (
                  <td key={scenario.id} className="px-4 py-3">
                    <span className={`text-sm font-semibold ${
                      scenario.revenue_delta_percent > 0 ? 'text-green-600' : 
                      scenario.revenue_delta_percent < 0 ? 'text-red-600' : 
                      'text-gray-600'
                    }`}>
                      {scenario.revenue_delta_percent > 0 ? '+' : ''}
                      {scenario.revenue_delta_percent.toFixed(2)}%
                    </span>
                  </td>
                ))}
              </tr>

              {/* Part Metrics */}
              <tr className="bg-purple-50">
                <td colSpan={scenarios.length + 1} className="px-4 py-2 text-sm font-semibold text-purple-900">
                  <Package className="inline h-4 w-4 mr-1" />
                  Scenario Scope
                </td>
              </tr>
              
              <tr>
                <td className="px-4 py-3 text-sm text-gray-700 font-medium">Parts Count</td>
                {scenarios.map(scenario => (
                  <td key={scenario.id} className="px-4 py-3 text-sm text-gray-900">
                    {scenario.part_count} parts
                  </td>
                ))}
              </tr>
              
              <tr className="bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-700 font-medium">Created Date</td>
                {scenarios.map(scenario => (
                  <td key={scenario.id} className="px-4 py-3 text-sm text-gray-600">
                    {new Date(scenario.created_at).toLocaleDateString()}
                  </td>
                ))}
              </tr>
              
              <tr>
                <td className="px-4 py-3 text-sm text-gray-700 font-medium">Last Updated</td>
                {scenarios.map(scenario => (
                  <td key={scenario.id} className="px-4 py-3 text-sm text-gray-600">
                    {new Date(scenario.updated_at).toLocaleDateString()}
                  </td>
                ))}
              </tr>

              {/* Description */}
              <tr className="bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-700 font-medium align-top">Description</td>
                {scenarios.map(scenario => (
                  <td key={scenario.id} className="px-4 py-3 text-sm text-gray-600">
                    {scenario.description || <span className="italic text-gray-400">No description</span>}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        {/* Winner Indicator */}
        {scenarios.length > 1 && (
          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <h3 className="font-semibold text-green-900 mb-2">💡 Best Revenue Impact</h3>
            <p className="text-sm text-green-800">
              {(() => {
                const best = scenarios.reduce((best, current) => 
                  current.revenue_delta_cents > best.revenue_delta_cents ? current : best
                );
                return (
                  <>
                    <strong>{best.name}</strong> has the highest revenue impact with{' '}
                    <strong>{formatCurrency(best.revenue_delta_cents)}</strong>
                    {' '}({best.revenue_delta_percent > 0 ? '+' : ''}{best.revenue_delta_percent.toFixed(2)}%)
                  </>
                );
              })()}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
